import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { config } from '../config';
import { clearSessionContext } from '../session';
import { sanitizeRedirectParam } from '../utils/sanitizeRedirectUrl';
import { getSessionStorageItemSafely, removeSessionStorageItemSafely, setSessionStorageItemSafely } from '../utils/storage';

const REDIRECT_STORAGE_KEY = 'auth_redirect';

/**
 * Perform final redirect after successful authentication.
 * Clears stored redirect and navigates to the target URL.
 */
export const completeAuthRedirect = () => {
  const redirect = sanitizeRedirectParam(getSessionStorageItemSafely(REDIRECT_STORAGE_KEY)) ?? config.defaultRedirectUrl;
  removeSessionStorageItemSafely(REDIRECT_STORAGE_KEY);
  // This visit's entry point is now stale for any later, unrelated visit this tab makes to
  // auth-app (e.g. redirected back here after a platform-app session timeout).
  clearSessionContext();
  window.location.replace(redirect);
};

/**
 * Stores the `redirect` query param (from /oauth/authorize) into sessionStorage.
 * Cleans the URL after storing.
 *
 * Call this hook on the login page to capture where to redirect after auth.
 * Use `completeAuthRedirect()` after successful login to navigate there.
 */
export const useStoreRedirectParam = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const redirect = searchParams.get('redirect');

    if (redirect) {
      setSessionStorageItemSafely(REDIRECT_STORAGE_KEY, redirect);
      searchParams.delete('redirect');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
};

/**
 * Peeks (without clearing) the stored `redirect` value — the full
 * /oauth/authorize URL captured by useStoreRedirectParam — validated the
 * same way completeAuthRedirect() validates it (same-origin only).
 *
 * Used to bounce an SSO failure back through /oauth/authorize (which
 * validates client_id/redirect_uri against the registered-client allowlist
 * before ever redirecting externally) rather than auth-app deciding for
 * itself whether an external redirect_uri is safe to navigate to. Does not
 * remove the stored value — completeAuthRedirect() still owns consuming it
 * on the success path.
 */
export const peekStoredAuthorizeUrl = (): string | null => sanitizeRedirectParam(getSessionStorageItemSafely(REDIRECT_STORAGE_KEY));
